import { Context as ReactContext } from "react";
import { CheckboxVisibility, DetailsListLayoutMode, DetailsRow, IColumn, ICommandBarItemProps, IconButton, IDetailsRowProps, MessageBar, MessageBarType, SelectionMode, ShimmeredDetailsList, TextField, TooltipHost } from "@fluentui/react";
import { ServicePrincipal, UserAccessTypes, SubscriberRecord } from "../../../Common/ConsumerModels/SubscriberRecord";
import { Dispatch, FormEvent, SetStateAction, useState } from "react";
import { HRDPEnvironment } from "../../../Common/CommonModels/HRDPEnvironmentEnum";
import * as Styled from '../../../Common/Styles/portalPage.styled';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import * as React from "react";

type AddServicePrincipalsContainerProps = {
  environment: HRDPEnvironment,
  userAccessTypes: UserAccessTypes[],
  setUserAccessTypes: Dispatch<SetStateAction<UserAccessTypes[]>>
}

export function AddServicePrincipalsContainer(props: AddServicePrincipalsContainerProps): React.ReactElement {
  const { httpClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);

  // Temparory variables
  const [inputedObjectId, setInputedObjectId] = useState<string>('');
  const [inputedClientId, setInputedClientId] = useState<string>('');
  const [isObjectIdPopulated, setIsObjectIdPopulated] = useState<boolean>(false);
  const [isClientIdPopulated, setIsClientIdPopulated] = useState<boolean>(false);
  const [existingConsumers, setExistingConsumers] = useState<SubscriberRecord[]>([]);
  const [isChecking, setIsChecking] = useState(false);

  // Error state variables
  const [inputedObjectIdError, setInputedObjectIdError] = useState<boolean>(false);
  const [isObjectIdUsedError, setIsObjectIdUsedError] = useState<boolean>(false);
  const [inputedClientIdError, setInputedClientIdError] = useState<boolean>(false);
  const [duplicateIdError, setDuplicateIdError] = useState<boolean>(false);
  const [fetchEntraIdError, setFetchEntraIdError] = useState<boolean>(false);

  // Loading State
  const [isInputedObjectIdLoading, setInputedObjectIdLoading] = useState(false);
  const [isInputedClientIdLoading, setInputedClientIdLoading] = useState(false);

  const onChangeInputedClientID = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setInputedClientId(newValue);
    setDuplicateIdError(false);
    setFetchEntraIdError(false);

    if (newValue == '') {
      setIsClientIdPopulated(false);
      setInputedClientIdError(false);
    }
    else {
      setIsClientIdPopulated(true);
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(newValue)) {
        isServicePrincipalClientIDAssigned(newValue);
      }
    }
  };

  const onChangeInputedObjectID = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setInputedObjectId(newValue);
    setDuplicateIdError(false);
    setFetchEntraIdError(false);

    if (newValue == '') {
      setInputedObjectIdError(false);
      setIsObjectIdPopulated(false);
    } else {
      setIsObjectIdPopulated(true);
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(newValue)) {
        isServicePrincipalObjectIDAssigned(newValue);
      }
    }
  };

  const fetchApplicationByID = async (newValue: string) => {
    return new Promise((resolve) => {
      httpClient
        .get(`${__API_BASE_URL__}/common/getApplicationById/${newValue}`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
        })
        .then((response: any) => {
          if (response.status == "200") {
            const inputedSrvcPrin: ServicePrincipal = {
              clientId: newValue,
              objectId: response.data.id,
              name: response.data.displayName
            }
            setFetchEntraIdError(false);
            resolve(inputedSrvcPrin);
          }
          else {
            setFetchEntraIdError(true);
            resolve(null);
          }
        })
        .catch(() => {
          setFetchEntraIdError(true);
          resolve(null);
        });
    });
  };

  const fetchDetailsByObjectID = async (newValue: string) => {
    return new Promise((resolve) => {
      httpClient
        .get(`${__API_BASE_URL__}/common/getDetailsByObjectId/${newValue}`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
        })
        .then((response: any) => {
          if (response.status == "200") {
            const inputedSrvcPrin: ServicePrincipal = {
              objectId: newValue,
              clientId: response.data.appId,
              name: response.data.displayName
            }
            setFetchEntraIdError(false);
            resolve(inputedSrvcPrin);
          }
          else {
            setFetchEntraIdError(true);
            resolve(null);
          }
        })
        .catch(() => {
          setFetchEntraIdError(true);
          resolve(null);
        });
    });
  };

  const getInputedClientIdErrorMessage = async (newValue?: string) => {
    if (newValue == '') {
      setInputedObjectIdError(false);
      return '';
    }
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(newValue)) {
      setInputedClientIdError(true);
      return 'Please enter a valid Client ID in GUID format.';
    }
    setInputedObjectIdError(false);
    return '';
  };

  const getInputedObjectIdErrorMessage = async (newValue?: string) => {
    if (newValue == '') {
      setInputedObjectIdError(false);
      return '';
    }

    if (newValue != '' && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(newValue)) {
      setInputedObjectIdError(true);
      return 'Please enter a valid Object ID in GUID format.';
    }
    setInputedObjectIdError(false);
    return '';
  };

  const fetchDetailsAndAddtoServicePrincipalList = async () => {
    var inputedServicePrincipal: ServicePrincipal = null;
    var existingUserAccessTypes = props.userAccessTypes?.filter(x => x.environment == props.environment);
    var existingServicePrincipals = existingUserAccessTypes.length > 0 ? existingUserAccessTypes[0].servicePrincipals : null;
    if (inputedObjectId != '') {
      // Verify whether the Service principal is already added
      for (let i = 0; i < existingServicePrincipals?.length; i++) {
        if (existingServicePrincipals[i].objectId == inputedObjectId) {
          setDuplicateIdError(true);
          return;
        }
      }
      setInputedObjectIdLoading(true);
      inputedServicePrincipal = (await fetchDetailsByObjectID(inputedObjectId.trim())) as ServicePrincipal;
    } else if (inputedClientId != '') {
      // Verify whether the Service principal is already added
      for (let i = 0; i < existingServicePrincipals?.length; i++) {
        if (existingServicePrincipals[i].clientId == inputedClientId) {
          setDuplicateIdError(true);
          return;
        }
      }
      setInputedClientIdLoading(true);
      inputedServicePrincipal = (await fetchApplicationByID(inputedClientId.trim())) as ServicePrincipal;
    }
    // Input IDs doesn't exist or ID Fetcher throws error
    if (inputedServicePrincipal == null || inputedClientId.trim() == '' && inputedObjectId.trim() == '') {
      // throw error message
      setInputedObjectIdLoading(false);
      setInputedClientIdLoading(false);
      return;
    }

    // If UserAccessType object is not created
    if (props.userAccessTypes == null || props.userAccessTypes.length == 0) {
      props.setUserAccessTypes([{
        environment: props.environment,
        servicePrincipals: [inputedServicePrincipal]
      }]);
    }
    // If UserAccessType object is not created for this specific environment
    else if (props.userAccessTypes.filter(x => x.environment == props.environment).length == 0) {
      props.setUserAccessTypes([...props.userAccessTypes, {
        environment: props.environment,
        servicePrincipals: [inputedServicePrincipal]
      }]);
    }
    else {
      // If UserAccessType object is created, then adding Service principal to the new or existing list
      props.setUserAccessTypes(accessTypes => {
        return accessTypes.map(accessType => {
          if (accessType.environment != props.environment) {
            return accessType;
          }

          var tempAccessType = accessType;
          // If accessType of specific environment is found, then Modifying the service Principals list
          if (accessType.servicePrincipals == null || accessType.servicePrincipals.length == 0) {
            tempAccessType.servicePrincipals = [inputedServicePrincipal]
          } else {
            tempAccessType.servicePrincipals = [...accessType.servicePrincipals, inputedServicePrincipal];
          }
          return tempAccessType;
        })
      })
    }
    setInputedObjectId('');
    setInputedClientId('');
    setIsObjectIdPopulated(false);
    setIsClientIdPopulated(false);
    setInputedObjectIdLoading(false);
    setInputedClientIdLoading(false);
    setIsObjectIdUsedError(false);
    return;
  }

  const validateAndAddObjectId = async () => {
    if (inputedObjectId != '') {
      const isUsed = await isObjectIdUsed()
      if (isUsed) return
    }
    fetchDetailsAndAddtoServicePrincipalList()
  }

  const isObjectIdUsed = async (): Promise<boolean> => {
    const payload = {
      environment: props.environment,
      objectId: inputedObjectId
    };
    try {
      const response = await httpClient.post(`${__API_BASE_URL__}/subscriber/isObjectIdUsed`, {
        resource: __CLIENT_ID__,
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Type-Options': 'nosniff',
        },
        data: payload,
      });

      if (response.status == 200) {
        setIsObjectIdUsedError(response.data as boolean);
        return response.data as boolean;
      } else {
        return true;
      }
    } catch (error) {
      console.error("Error checking if Object ID is used:", error);
      return true;
    }
  }

  const isServicePrincipalObjectIDAssigned = async (objectId: string): Promise<boolean> => {
    if (!objectId || objectId == "") {
      setIsObjectIdUsedError(false);
      setExistingConsumers([]);
      return false;
    }

    const payload = {
      environment: props.environment,     // 0/1
      objectId: objectId
    };
    setIsChecking(true);

    try {
      const response = await httpClient.post<SubscriberRecord[]>(
        `${__API_BASE_URL__}/subscriber/isServicePrincipalObjectIDAssigned`, {
        resource: __CLIENT_ID__,
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Type-Options': 'nosniff',
        },
        data: payload,
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : [];
        setExistingConsumers(data);
        const isUsed = data.length > 0;
        setIsObjectIdUsedError(isUsed);

        return isUsed;
      }

      setExistingConsumers([]);
      setIsObjectIdUsedError(false);
      return false;
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message ?? err?.message;
      console.error("Error checking if Object ID is used:", { status, serverMessage, err });

      setExistingConsumers([]);
      setIsObjectIdUsedError(false);

      return false;

    } finally {
      setIsChecking(false);
    }

  }

  const isServicePrincipalClientIDAssigned = async (clientId: string): Promise<boolean> => {
    if (!clientId || clientId == "") {
      setIsObjectIdUsedError(false);
      setExistingConsumers([]);
      return false;
    }
    const payload = {
      environment: props.environment,
      clientId: clientId
    };
    setIsChecking(true);

    try {
      const response = await httpClient.post<SubscriberRecord[]>(
        `${__API_BASE_URL__}/subscriber/isServicePrincipalClientIDAssigned`, {
        resource: __CLIENT_ID__,
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Type-Options': 'nosniff',
        },
        data: payload,
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : [];
        setExistingConsumers(data);
        const isUsed = data.length > 0;
        setIsObjectIdUsedError(isUsed);

        return isUsed;
      }

      setExistingConsumers([]);
      setIsObjectIdUsedError(false);
      return false;
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message ?? err?.message;
      console.error("Error checking if Client ID is used:", { status, serverMessage, err });

      setExistingConsumers([]);
      setIsObjectIdUsedError(false);

      return false;
    } finally {
      setIsChecking(false);
    }

  }

  const userAccessCommandBarItems: ICommandBarItemProps[] = [
    {
      key: 'addServicePrincipal',
      text: 'Add',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        validateAndAddObjectId()
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    }
  ]



  const [servicePrincipalTableColumns, setServicePrincipalTableColumns] = React.useState<IColumn[]>([
    {
      key: 'deleteIcon',
      name: '',
      minWidth: 10,
      maxWidth: 30,
      isResizable: true
    },
    {
      key: 'objectId',
      name: 'Object ID',
      minWidth: 100,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'clientId',
      name: 'Client ID',
      minWidth: 100,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'name',
      name: 'Service Principal Name',
      minWidth: 100,
      maxWidth: 250,
      isResizable: true,
    },
  ]);

  const onHandleDeleteServicePrincipal = (item: ServicePrincipal): void => {
    props.setUserAccessTypes(accessTypes => {
      return accessTypes.map(accessType => {
        if (accessType.environment != props.environment) {
          return accessType;
        }
        var tempAccessType = accessType;
        // If accessType of specific environment is found, then Modifying the service Principals list
        tempAccessType.servicePrincipals = accessType.servicePrincipals.filter(x => x.clientId != item.clientId && x.objectId != item.objectId);
        return tempAccessType;
      })
    })
  }

  const onRenderItemColumn = (item: ServicePrincipal, index: number, column: IColumn): React.ReactNode => {
    if (column.key == 'deleteIcon') {
      return (
        <div aria-label={`Column header: ${column.key}`}>
          <TooltipHost
            content="Delete Consumer"
            id="deleteRowIcon"
            styles={{ root: { display: 'inline-block' } }}
          >
            <IconButton iconProps={{ iconName: 'Delete' }} aria-label="Delete Service Principal" onClick={() => onHandleDeleteServicePrincipal(item)} />
          </TooltipHost>
        </div>
      );
    }
    const value = item[column.key as keyof ServicePrincipal];
    return <>{value}</>;
  };

  const onRenderRow = (props: IDetailsRowProps) => {
    return <DetailsRow aria-busy={true} {...props} />;
  };

  return (
    <>
      <Styled.SingleRowContainer>
        <label>Enter Object ID or Client ID &nbsp; &nbsp;</label>
        <TextField
          aria-label="Object ID"
          id="objectID"
          data-testid="service_principal_object_id"
          placeholder="Enter Object ID"
          value={inputedObjectId}
          onChange={onChangeInputedObjectID}
          styles={{
            field: { color: '#323130' },
            subComponentStyles: { label: { root: { color: '#323130' } } },
          }}
          disabled={isClientIdPopulated}
          onGetErrorMessage={getInputedObjectIdErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
        />
        <label>&nbsp;(or)&nbsp;</label>
        <TextField
          aria-label="Client ID"
          id="clientID"
          placeholder="Enter Client ID"
          value={inputedClientId}
          onChange={onChangeInputedClientID}
          styles={{
            field: { color: '#323130' },
            subComponentStyles: { label: { root: { color: '#323130' } } },
          }}
          disabled={isObjectIdPopulated}
          onGetErrorMessage={getInputedClientIdErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
        />
        <Styled.PlainCommandBar items={userAccessCommandBarItems} ariaLabel="Command Bar" />
      </Styled.SingleRowContainer>
      {fetchEntraIdError && (
        <MessageBar data-testid="message-bar-pivot-change-error-1" messageBarType={MessageBarType.blocked}>
          The provided Object/Client ID is invalid and does not exist in Azure Entra.
        </MessageBar>
      )}
      {duplicateIdError && (
        <MessageBar data-testid="message-bar-pivot-change-error-2" messageBarType={MessageBarType.blocked}>
          The provided Object/Client ID has already been added to the Service Principals list.
        </MessageBar>
      )}
      {isObjectIdUsedError && (
        <MessageBar
          data-testid="message-bar-pivot-change-error-3"
          messageBarType={MessageBarType.blocked}
          isMultiline
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            The provided Object/Client ID has already been used by below consumer{existingConsumers?.length > 1 ? "s" : ""}.
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #ddd" }}>
                  Consumer Id
                </th>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #ddd" }}>
                  Consumer Name
                </th>
              </tr>
            </thead>
            <tbody>
              {(existingConsumers ?? []).map((c) => (
                <tr key={c.id}>
                  <td style={{ padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>
                    {c.id}
                  </td>
                  <td style={{ padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>
                    {c.name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </MessageBar>
      )}
      <ShimmeredDetailsList
        items={props.userAccessTypes?.filter(x => x.environment == props.environment)[0]?.servicePrincipals ?? []}
        enableShimmer={isInputedObjectIdLoading || isInputedClientIdLoading}
        columns={servicePrincipalTableColumns}
        //onRenderItemColumn={onRenderItemColumn}
        onRenderItemColumn={(item, index, column): React.ReactNode => { return onRenderItemColumn(item, index, column) }}
        onRenderRow={onRenderRow}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.justified}
        checkboxVisibility={CheckboxVisibility.hidden}
        ariaLabelForShimmer="Service Principals are being fetched"
        ariaLabelForGrid="Service Principals Table"
      />
    </>);
}